import type { PostHogConfig } from "../provider-options";
import type {
	AnalyticsEventType,
	AnalyticsMetadata,
	AnalyticsValue,
	EventProperties,
	RegisteredProperties,
} from "../types";
import { BaseAnalyticsForwarder } from "./base-analytics";

type PostHogNode = import("posthog-node").PostHog;
type PostHogBrowser = typeof import("posthog-js").default;

type PostHogClient = PostHogNode | PostHogBrowser;

export type { PostHogBrowserOptions, PostHogConfig, PostHogNodeOptions } from "../provider-options";

export class PostHogAnalytics extends BaseAnalyticsForwarder {
	// Resolves to the loaded client, or `null` when the runtime adapter could
	// not be loaded. It never rejects, so no use site can emit an unhandled
	// rejection and analytics stays a strict no-op when unavailable.
	private readonly clientReady: Promise<PostHogClient | null>;
	private readonly isNode: boolean;

	constructor(apiKey: string, host?: string, options?: PostHogConfig) {
		super();

		this.isNode = typeof window === "undefined";

		const load = this.isNode
			? this.createNodeClient(apiKey, host, options)
			: this.createBrowserClient(apiKey, host, options);

		this.clientReady = load.catch(() => null);
	}

	// ESM-safe dynamic import — no `eval("require")`. Bundlers resolve the
	// browser entry only; the server adapter is code-split behind this import.
	private async createNodeClient(
		apiKey: string,
		host: string | undefined,
		options?: PostHogConfig,
	): Promise<PostHogClient> {
		const mod = await import("posthog-node");
		return new mod.PostHog(apiKey, {
			host,
			flushAt: 1,
			flushInterval: 0,
			...options?.nodeOptions,
		});
	}

	private async createBrowserClient(
		apiKey: string,
		host: string | undefined,
		options?: PostHogConfig,
	): Promise<PostHogClient> {
		const mod = await import("posthog-js");
		const posthog = mod.default;
		if (!posthog.__loaded) {
			posthog.init(apiKey, {
				api_host: host,
				...options?.browserOptions,
			});
		}
		return posthog;
	}

	// Every side-effecting call routes through the caught, memoized promise so a
	// failed adapter load degrades to a no-op instead of an unhandled rejection.
	private withClient(callback: (client: PostHogClient) => void): void {
		this.clientReady
			.then((client) => {
				if (client) {
					callback(client);
				}
			})
			.catch(() => {});
	}

	track<T extends AnalyticsEventType>(event: T, properties: EventProperties<T>, metadata?: AnalyticsMetadata): void {
		const eventName = this.formatEventName(event, properties as Record<string, AnalyticsValue>);
		const allProperties = {
			...this.mergeProperties(properties, metadata),
			event_type: event,
		};

		this.withClient((client) => {
			if (this.isNode) {
				const sessionId = this.registeredProperties.sessionId || "anonymous";
				(client as PostHogNode).capture({
					distinctId: sessionId,
					event: eventName,
					properties: allProperties,
				});
				return;
			}
			(client as PostHogBrowser).capture(eventName, allProperties);
			if (this.registeredProperties.sessionId) {
				(client as PostHogBrowser).identify(this.registeredProperties.sessionId);
			}
		});
	}

	identify(): void {
		const sessionId = this.registeredProperties.sessionId || "anonymous";

		this.withClient((client) => {
			if (this.isNode) {
				(client as PostHogNode).identify({ distinctId: sessionId });
				return;
			}
			(client as PostHogBrowser).identify(sessionId);
		});
	}

	async flush(): Promise<void> {
		const client = await this.clientReady;
		if (client && this.isNode) {
			await (client as PostHogNode).flush();
		}
		// Browser doesn't have a flush method; events are auto-flushed.
	}

	register(properties: RegisteredProperties): void {
		this.registeredProperties = properties;

		if (this.isNode) {
			// Node version doesn't support register; properties are passed per event.
			return;
		}
		this.withClient((client) => {
			if ((client as PostHogBrowser).register) {
				(client as PostHogBrowser).register(properties);
			}
		});
	}

	startSessionRecording(): void {
		if (this.isNode) {
			// Node version doesn't support session recording.
			return;
		}
		this.withClient((client) => {
			if ((client as PostHogBrowser).startSessionRecording) {
				(client as PostHogBrowser).startSessionRecording();
			}
		});
	}
}
